import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from '@/components/Button';
import { CreateFolderButton } from '@/components/CreateFolder';
import Dialog, {
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/Dialog';
import { RadioGroup, RadioGroupItem } from '@/components/RadioGroup';
import ScrollArea from '@/components/ScrollArea';
import VisuallyHidden from '@/components/VisuallyHidden';
import { useMediaQuery, useShallowEqualSelector } from '@/hooks';
import Close from '@/icons/close.svg';
import NoFolder from '@/icons/no-folder.svg';
import Search from '@/icons/search.svg';
import {
	batchMoveTransactions,
	batchRemoveTransactions
} from '@/store/myDocuments/thunks';
import { cn, showError, showSuccess } from '@/utils';

function SaveToFolderModal({ open, onOpenChange, selectedTransactions }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const sm = useMediaQuery('sm');
	const { folders } = useShallowEqualSelector(({ myDocuments }) => ({
		folders: myDocuments.folders || []
	}));
	const [selectedFolder, setSelectedFolder] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [loading, setLoading] = useState(false);

	const filteredFolders = useMemo(() => {
		if (!searchTerm) {
			return folders;
		}

		return folders.filter(folder =>
			folder.name.toLowerCase().includes(searchTerm.toLowerCase())
		);
	}, [folders, searchTerm]);

	const handleSaveToFolder = async () => {
		setLoading(true);
		dispatch(
			batchMoveTransactions({
				transactions: selectedTransactions.map(
					transaction => transaction.id
				),
				folder: selectedFolder
			})
		)
			.unwrap()
			.then(() => {
				onOpenChange(false);
				setLoading(false);
				showSuccess(t('myDocuments.toast.saveDocumentSuccess'));
			})
			.catch(error => {
				showError(error);
				setLoading(false);
			});
	};

	const handleRemoveFromFolder = async () => {
		dispatch(
			batchRemoveTransactions({
				transactions: selectedTransactions.map(
					transaction => transaction.id
				)
			})
		)
			.unwrap()
			.then(() => {
				onOpenChange(false);
				showSuccess(t('editor.saveSuccess'));
			})
			.catch(error => {
				showError(error);
			});
	};

	const isScrollVisible =
		(sm && filteredFolders?.length > 2) ||
		(!sm && filteredFolders?.length > 4);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="w-[280px] flex-col md:w-[397px]">
				<DialogHeader className="flex h-12 flex-row items-center justify-between border-b border-grey-200 px-4">
					<DialogTitle className="font-title text-xs font-semibold leading-5 md:text-sm md:leading-6">
						{t('myDocuments.modals.saveToFolder.title')}
					</DialogTitle>
					<VisuallyHidden asChild>
						<DialogDescription>
							{t('myDocuments.modals.saveToFolder.description')}
						</DialogDescription>
					</VisuallyHidden>
				</DialogHeader>

				<div className="flex flex-col gap-2 p-4">
					<p className="font-title text-xs leading-5 text-grey-500">
						{t('myDocuments.modals.saveToFolder.description')}
					</p>
					<div className="flex w-full items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm transition-colors placeholder:text-gray-500 focus:border-black focus-visible:outline-none">
						<Search
							className="h-6 w-6 flex-shrink-0 text-black"
							width="24"
							height="24"
						/>
						<input
							type="text"
							value={searchTerm}
							onChange={event => setSearchTerm(event.target.value)}
							className="w-full flex-shrink focus-visible:outline-none"
						/>
						<Button
							size="iconMd"
							variant="outline"
							className={`h-6 w-6 border-0 p-0 ${!searchTerm && 'hidden'}`}
							onClick={() => setSearchTerm('')}
						>
							<Close size={17} />
						</Button>
					</div>
					<Button
						variant="ghost"
						className="flex items-center gap-2 self-start px-2 py-1"
						onClick={handleRemoveFromFolder}
					>
						<NoFolder />
						<span className="font-title text-xs font-semibold leading-5">
							{t('myDocuments.modals.saveToFolder.noFolder')}
						</span>
					</Button>
					<ScrollArea
						className={isScrollVisible ? 'pr-4' : ''}
						maxHeight={sm ? 128 : 260}
					>
						<RadioGroup
							onValueChange={value => {
								setSelectedFolder(value);
							}}
							value={selectedFolder}
						>
							{filteredFolders.map(folder => {
								return (
									<div
										key={folder.id}
										className={cn(
											'group mb-1 flex w-full cursor-pointer items-center justify-between rounded border px-3 py-2 text-sm transition-colors hover:border-gray-300 hover:bg-grey-100 focus:border-black focus-visible:outline-none',
											selectedFolder === folder.id
												? 'border-red bg-red-background'
												: 'border-gray-200 bg-white'
										)}
										onClick={() => setSelectedFolder(folder.id)}
									>
										<span
											className={cn(
												'text-sm leading-6 group-hover:text-black',
												selectedFolder === folder.id
													? 'text-red'
													: 'text-grey-700'
											)}
										>
											{folder.name}
										</span>
										<RadioGroupItem
											value={folder.id}
											id={folder.id}
											checked={selectedFolder === folder.id}
										/>
									</div>
								);
							})}
						</RadioGroup>
					</ScrollArea>
				</div>
				<DialogFooter>
					<CreateFolderButton withIcon />
					<Button
						onClick={handleSaveToFolder}
						loading={loading}
						disabled={!selectedFolder}
					>
						{t('common.save')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
export default SaveToFolderModal;
